<template lang="pug">
.global-chat
  .chat-shell
    .loading.text-secondary(v-if="loading") {{ $t("message.loadingChat") }}
    .messages-list(v-else ref="messagesList" @scroll="onMessagesScroll")
      .message-row(v-for="(item, idx) in messages" :key="item._id || `${item.createdAt || 'm'}-${idx}`" :class="{ mine: item.senderProfileId === myProfileId }")
        .message-bubble(
          @mousedown="pressedMessageId = (isTranslated(item) ? (item._id || '') : '')"
          @mouseup="pressedMessageId = ''"
          @mouseleave="pressedMessageId = ''"
          @touchstart="pressedMessageId = (isTranslated(item) ? (item._id || '') : '')"
          @touchend="pressedMessageId = ''"
        )
          .message-sender {{ item.senderName || "Anonymous" }}
          .message-text(:class="{ 'message-text-ar': getCurrentLanguage().substring(0, 2).toLowerCase() === 'ar' }") {{ getMessageText(item) }}
          BibleEmbeddedView(:content="getMessageRawContent(item)" compact)
          .message-note(v-if="isTranslated(item)") * {{ $t("message.aiTranslated") }}
          .message-time {{ formatTime(item.createdAt) }}
        .message-media(v-if="getYouTubeVideoId(item)")
          iframe.yt-embed(
            :src="getYouTubeEmbedUrl(item)"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          )

    .composer
      textarea.form-control(
        v-model="text"
        :placeholder="$t('message.writeMessage')"
        rows="2"
        maxlength="500"
        @keydown.enter.exact.prevent="sendMessage"
      )
      .d-flex.justify-content-end.mt-2
        button.btn.btn-primary(:disabled="sending || !text.trim()" @click="sendMessage") {{ $t("message.send") }}
</template>

<script>
import API from "../API.js";
import { UserStore } from "../UserStore.js";
import BibleEmbeddedView from "../components/BibleEmbeddedView.vue";
import { stripBibleTokens } from "../utils/bibleReferences.js";

export default {
  name: "GlobalChat",
  components: {
    BibleEmbeddedView,
  },
  data() {
    return {
      messages: [],
      text: "",
      loading: true,
      sending: false,
      refreshTimer: null,
      pressedMessageId: "",
      initialScrolled: false,
      wasAtBottom: true,
      myProfileId: (UserStore.me && UserStore.me._id) || "",
    };
  },
  methods: {
    getCurrentLanguage() {
      const locale = this.$i18n && this.$i18n.locale;
      if (typeof locale === "string") return locale;
      if (locale && typeof locale.value === "string") return locale.value;
      return "en";
    },
    formatTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleTimeString();
    },
    isTranslated(message) {
      return !!(message && message.textOriginal && message.text && message.textOriginal !== message.text);
    },
    getMessageText(message) {
      if (!this.isTranslated(message)) return stripBibleTokens((message && message.text) || "");
      const messageId = (message && message._id) || "";
      if (this.pressedMessageId && this.pressedMessageId === messageId) {
        return stripBibleTokens((message && message.textOriginal) || "");
      }
      return stripBibleTokens((message && message.text) || "");
    },
    getMessageRawContent(message) {
      return (message && (message.textOriginal || message.text)) || "";
    },
    getYouTubeVideoIdFromText(text) {
      const safeText = (text || "") + "";
      const matches = safeText.match(/https?:\/\/[^\s]+/gi) || [];
      for (let i = 0; i < matches.length; i += 1) {
        const rawUrl = matches[i].replace(/[),.;!?]+$/, "");
        try {
          const parsed = new URL(rawUrl);
          const host = (parsed.hostname || "").replace(/^www\./, "");
          if (host === "youtu.be") {
            const shortId = ((parsed.pathname || "").replace("/", "").split("/")[0] || "").trim();
            if (shortId) return shortId;
          }
          if (host === "youtube.com" || host === "m.youtube.com") {
            const watchId = parsed.searchParams.get("v");
            if (watchId) return watchId;
            const pathParts = (parsed.pathname || "").split("/").filter(Boolean);
            if ((pathParts[0] === "shorts" || pathParts[0] === "embed") && pathParts[1]) {
              return pathParts[1];
            }
          }
        } catch (error) {
          continue;
        }
      }
      return "";
    },
    getYouTubeVideoId(message) {
      const originalId = this.getYouTubeVideoIdFromText(message && message.textOriginal);
      if (originalId) return originalId;
      return this.getYouTubeVideoIdFromText(message && message.text);
    },
    getYouTubeEmbedUrl(message) {
      const videoId = this.getYouTubeVideoId(message);
      if (!videoId) return "";
      return "https://www.youtube.com/embed/" + encodeURIComponent(videoId) + "?fs=0";
    },
    scrollToBottom(force = false) {
      this.$nextTick(() => {
        const container = this.$refs.messagesList;
        if (!container) return;
        if (force || this.wasAtBottom || !this.initialScrolled) {
          container.scrollTop = container.scrollHeight;
          this.initialScrolled = true;
        }
      });
    },
    onMessagesScroll(event) {
      const el = event && event.target;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      this.wasAtBottom = distanceFromBottom < 40;
    },
    async loadMessages() {
      const result = await API.getChatMessages(100, this.getCurrentLanguage());
      this.messages = Array.isArray(result) ? result : [];
      this.scrollToBottom();
    },
    async bootstrap() {
      this.loading = true;
      const messages = await API.getChatMessages(100, this.getCurrentLanguage());
      this.messages = Array.isArray(messages) ? messages : [];
      this.loading = false;
      this.scrollToBottom(true);
    },
    async sendMessage() {
      const body = (this.text || "").trim();
      if (!body || this.sending) return;
      this.sending = true;
      const response = await API.sendChatMessage(body, this.getCurrentLanguage());
      if (response && response.status === "ok" && response.message) {
        this.messages.push(response.message);
        if (Array.isArray(response.activeUsers)) {
          this.activeUsers = response.activeUsers;
        }
        this.text = "";
        this.wasAtBottom = true;
        this.scrollToBottom(true);
      }
      this.sending = false;
    },
  },
  async mounted() {
    if (!(UserStore.me && UserStore.me._id)) {
      await UserStore.getMe();
    }
    this.myProfileId = (UserStore.me && UserStore.me._id) || "";
    await this.bootstrap();

    this.refreshTimer = setInterval(() => {
      this.loadMessages();
    }, 8000);
  },
  beforeUnmount() {
    clearInterval(this.refreshTimer);
  },
};
</script>

<style scoped lang="scss">
.global-chat {
  height: 100vh;
  overflow: hidden;

  .chat-shell {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border: 1px solid #eef0f2;
    border-radius: 12px;
    padding: 12px;
    background: #fafbfc;
  }

  .messages-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 2px;
  }

  .message-row {
    display: flex;
    margin-bottom: 10px;

    &.mine {
      justify-content: flex-end;

      .message-bubble {
        background: #0d6efd;
        color: #fff;

        .message-sender,
        .message-note,
        .message-time {
          color: #dbeafe;
        }
      }
    }
  }

  .message-bubble {
    max-width: 82%;
    text-align: left;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 8px 10px;
  }

  .message-media {
    width: min(82%, 460px);
    margin-top: 6px;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    aspect-ratio: 16 / 9;
  }

  .yt-embed {
    width: 100%;
    height: 100%;
    min-height: 220px;
  }

  .message-sender {
    font-size: 12px;
    color: #68707b;
    margin-bottom: 3px;
  }

  .message-text-ar {
    text-align: right;
  }

  .message-note,
  .message-time {
    font-size: 11px;
    color: #7b8591;
    margin-top: 4px;
  }

  .composer textarea {
    resize: none;
  }
}
</style>
